import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import i18n from "../../i18n"; 
import { apiCallBegan, ApiCallFailedPayload, ApiCallSuccessPayload } from "../api"; 
import { AppDispatch, AppState } from "../configureStore"; 
import SliceBase from "../types/SliceBase";
import { authHeader, getWorkerId } from "../users/auth"; 

export enum BusinessDocType {
    OTHER = "other",
    CHILD_RECOVERY_PERIOD = "childRecoveryPeriod",
    SCREENING = "screening",
    APPLICATION = "application"
}

// SLICE //
interface State {
    uploadFile: {
        id: number;
        fileName: string; // Stores uploaded file URL
        progress: number; // Progress percentage for upload
    } & SliceBase;
    downloadFile: {
        blobUrl: string; // Stores downloaded file URL (temporary blob URL)
    } & SliceBase;
}

const initialState: State = {
    uploadFile: {
        id: 0,
        fileName: "",
        progress: 0,
        loading: false,
        lastFetch: null,
        error: null
    },
    downloadFile: {
        blobUrl: "",
        loading: false,
        lastFetch: null,
        error: null
    }
};

const slice = createSlice({
    name: "fileStorage",
    initialState,
    reducers: {
        // UPLOAD FILE
        fileUploadRequested: (state) => {
            state.uploadFile.loading = true;
            state.uploadFile.progress = 0;
            state.uploadFile.error = null;
            state.uploadFile.fileName = ''; // Reset uploaded
            state.uploadFile.id = 0; // Reset id
        },
        fileUploadSuccess: (
            state,
            action: PayloadAction<ApiCallSuccessPayload>
        ) => {
            state.uploadFile.loading = false;
            state.uploadFile.progress = 100;
            state.uploadFile.lastFetch = Date.now();
            if (action.payload.data) {
                state.uploadFile.fileName = action.payload.data.fileName || action.payload.data.FileName;
                state.uploadFile.id = action.payload.data.id || action.payload.data.Id;
            }
        },
        fileUploadProgress: (state, action: PayloadAction<number>) => {
            state.uploadFile.progress = action.payload;
        },
        fileUploadFailed: (
            state,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            state.uploadFile.loading = false;
            state.uploadFile.progress = 0;
            state.uploadFile.error = action.payload;
            state.uploadFile.fileName = ''; // Reset src
            state.uploadFile.id = 0; // Reset id
        },

        // DOWNLOAD FILE
        fileDownloadRequested: (
            state
        ) => {
            state.downloadFile.loading = true;
            state.downloadFile.error = null;
            state.downloadFile.blobUrl = "";
        },
        fileDownloadSuccess: (
            state,
            action: PayloadAction<ApiCallSuccessPayload>
        ) => {
            state.downloadFile.loading = false;
            state.downloadFile.lastFetch = Date.now();
            if (action.payload) {
                // Revoke old blob URL if it exists
                if (state.downloadFile.blobUrl) {
                    window.URL.revokeObjectURL(state.downloadFile.blobUrl);
                }
                const { fileName } = action.payload.extraData as { id: number; fileName: string };
                triggerFileDownload(action.payload.data, fileName);
                state.downloadFile.blobUrl = action.payload.data as string;
            }
        },
        fileDownloadFailed: (
            state,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            state.downloadFile.loading = false;
            state.downloadFile.error = action.payload;
            // Revoke old blob URL if it exists
            if (state.downloadFile.blobUrl) {
                window.URL.revokeObjectURL(state.downloadFile.blobUrl);
            }
            state.downloadFile.blobUrl = "";
        },

        // DELETE FILE
        fileDeleteRequested: (state) => {
            state.downloadFile.loading = true;
            state.downloadFile.error = null;
        },
        fileDeleted: (state) => {
            // Revoke old blob URL if it exists
            if (state.downloadFile.blobUrl) {
                window.URL.revokeObjectURL(state.downloadFile.blobUrl);
            }
            state.downloadFile.blobUrl = "";
            state.downloadFile.loading = false;
            state.downloadFile.lastFetch = Date.now();
        },
        fileDeleteFailed: (
            state,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            state.downloadFile.loading = false;
            state.downloadFile.error = action.payload;
        },

        cleanupBlobUrl: (state) => {
            if (state.downloadFile.blobUrl) {
                window.URL.revokeObjectURL(state.downloadFile.blobUrl);
                state.downloadFile.blobUrl = "";
            }
        }
    }
});

const triggerFileDownload = (url: string, fileName: string) => {
    // const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    //window.URL.revokeObjectURL(url);
};

const {
    fileUploadRequested,
    fileUploadProgress,
    fileUploadSuccess,
    fileUploadFailed,
    fileDownloadRequested,
    fileDownloadSuccess,
    fileDownloadFailed,
    fileDeleteRequested,
    fileDeleted,
    fileDeleteFailed,
    cleanupBlobUrl,
} = slice.actions;

export default slice.reducer;

// SELECTORS
export const selectUploadFile = (state: AppState) =>
    state.entities.fileStorage.uploadFile;
export const selectUploadFileLoading = (state: AppState) =>
    state.entities.fileStorage.uploadFile.loading;
export const selectUploadFileProgress = (state: AppState) => state.entities.fileStorage.uploadFile.progress;
export const selectUploadFileError = (state: AppState) =>
    state.entities.fileStorage.uploadFile.error;
export const selectDownloadBlobUrl = (state: AppState) =>
    state.entities.fileStorage.downloadFile.blobUrl;
export const selectDownloadFileLoading = (state: AppState) =>
    state.entities.fileStorage.downloadFile.loading;
export const selectDownloadFileError = (state: AppState) =>
    state.entities.fileStorage.downloadFile.error;
export { cleanupBlobUrl };

// THUNKS
export const uploadFile =
    (file: File, userId?: number, docType: BusinessDocType = BusinessDocType.OTHER) => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.fileStorage.uploadFile.loading) {
            return;
        }

        if (!file) {
            const errorPayload: ApiCallFailedPayload = { code: 400, message: "No file selected" };
            dispatch({ type: fileUploadFailed.type, payload: errorPayload });
            return;
        }

        const workerId = userId ?? getWorkerId(getState());
        if (workerId == null) {
            const errorPayload: ApiCallFailedPayload = { code: 400, message: "Worker ID is missing" };
            dispatch({ type: fileUploadFailed.type, payload: errorPayload });
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("workerId", workerId.toString());
        if (docType) {
            formData.append("docType", docType);
        }

        const baseUrl = window._env_.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
        const apiPath = `/FileStorage/upload/${workerId}`;
        const url = `${baseUrl}${apiPath}`;

        return new Promise((resolve, reject) => {
            dispatch({ type: fileUploadRequested.type });

            const xhr = new XMLHttpRequest();
            xhr.open("POST", url, true);
            Object.entries(authHeader()).forEach(([key, value]) => {
                xhr.setRequestHeader(key, value);
            });
            xhr.setRequestHeader("Accept", "*/*");

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const progress = (e.loaded / e.total) * 100;
                    dispatch({ type: fileUploadProgress.type, payload: progress });
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        if (!response?.data?.fileName && !response?.data?.FileName) {
                            const errorPayload: ApiCallFailedPayload = { code: 400, message: "No file URL returned" };
                            dispatch({ type: fileUploadFailed.type, payload: errorPayload });
                            reject(new Error("No file URL returned"));
                            return;
                        }
                        dispatch({
                            type: fileUploadSuccess.type,
                            payload: { data: response.data },
                        });
                        resolve(response);
                    } catch (error) {
                        const errorPayload: ApiCallFailedPayload = { code: 400, message: "Invalid response format" };
                        dispatch({ type: fileUploadFailed.type, payload: errorPayload });
                        reject(error);
                    }
                } else {
                    const errorPayload: ApiCallFailedPayload = { code: xhr.status, message: xhr.responseText || "Upload failed" };
                    dispatch({ type: fileUploadFailed.type, payload: errorPayload });
                    reject(new Error(xhr.responseText || "Upload failed"));
                }
            };

            xhr.onerror = () => {
                const errorPayload: ApiCallFailedPayload = { code: 500, message: "Network error" };
                dispatch({ type: fileUploadFailed.type, payload: errorPayload });
                reject(new Error("Network error"));
            };

            xhr.send(formData);
        });
    };

export const downloadFile =
    (fileRefId: number, fileName: string) => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.fileStorage.downloadFile.loading) {
            return;
        }

        if (!fileRefId || fileRefId <= 0) {
            const errorPayload: ApiCallFailedPayload = {
                code: 400,
                message: "Invalid file reference ID",
                extraData: { id: fileRefId, fileName }
            };
            dispatch({ type: fileDownloadFailed.type, payload: errorPayload });
            return;
        }

        const workerId = getWorkerId(getState());

        return await dispatch(
            apiCallBegan({
                url: `/api/FileStorage/download/${fileRefId}/worker/${workerId}`,
                method: "GET",
                headers: {
                    ...authHeader(),
                    "Accept-Language": i18n.language,
                    Accept: "application/octet-stream"
                },
                extraData: { id: fileRefId, fileName },
                responseType: "blob",
                onStart: fileDownloadRequested.type,
                onSuccess: fileDownloadSuccess.type,
                onError: fileDownloadFailed.type,
            })
        );
    };

export const deleteFile =
    (fileRefId: number) => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.fileStorage.downloadFile.loading) {
            return;
        }

        if (!fileRefId || fileRefId <= 0) {
            const errorPayload: ApiCallFailedPayload = {
                code: 400,
                message: "Invalid file reference ID"
            };
            dispatch({ type: fileDeleteFailed.type, payload: errorPayload });
            return;
        }

        const workerId = getWorkerId(getState());

        return await dispatch(
            apiCallBegan({
                url: `/api/FileStorage/delete/${fileRefId}/worker/${workerId}`,
                method: "DELETE",
                headers: {
                    ...authHeader(),
                    "Accept-Language": i18n.language
                },
                onStart: fileDeleteRequested.type,
                onSuccess: fileDeleted.type,
                onError: fileDeleteFailed.type
            })
        );
    };